import { X } from "lucide-solid";
import { type JSX, Show, createEffect, createUniqueId, onCleanup } from "solid-js";
import { Portal } from "solid-js/web";

import { t } from "@/lib/i18n";
import { Button } from "./Button";

/**
 * Modale Scola — accessibilité WCAG 2.2 + microinteractions.
 *
 * Garanties conformes au manifeste produit (§3 Élégance, §9 Accessibilité,
 * §4 Fiabilité visible) :
 *
 * - Focus trap : Tab/Shift+Tab cycle sur les éléments focusables internes
 * - Auto-focus du premier élément focusable à l'ouverture
 * - Focus restoration : le focus revient sur l'élément déclencheur à la fermeture
 * - Scroll lock du body pendant l'ouverture
 * - ESC ferme (configurable via `dismissable`)
 * - Click sur le backdrop ferme (configurable)
 * - Transition fade + scale 150 ms ease-out (charte §3 : 150 ms par défaut)
 * - `role="dialog"` + `aria-modal="true"` + `aria-labelledby` + `aria-describedby`
 * - Backdrop avec token CSS (pas de rgba arbitraire)
 */
export function FormDialog(props: {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children: JSX.Element;
  footer?: JSX.Element;
  /** Bloquer ESC et click backdrop (utile pour confirmations critiques). */
  dismissable?: boolean;
  /** Largeur max (Tailwind class), défaut max-w-2xl. */
  maxWidth?: string;
}) {
  const titleId = createUniqueId();
  const descId = createUniqueId();
  let dialogRef: HTMLDivElement | undefined;
  let previousFocus: HTMLElement | null = null;

  const dismissable = () => props.dismissable !== false;

  // Focus management + scroll lock + ESC handler
  createEffect(() => {
    if (!props.open) return;

    // Mémoriser l'élément focusé avant ouverture pour le restaurer après
    previousFocus = document.activeElement as HTMLElement | null;

    // Scroll lock body — pas de saut visuel grâce à overflow:hidden + padding-right compensé
    const originalOverflow = document.body.style.overflow;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    // Auto-focus du premier élément focusable (frame suivante, après mount)
    queueMicrotask(() => {
      const first = focusableElements(dialogRef)[0];
      first?.focus();
    });

    // Handlers clavier
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && dismissable()) {
        e.preventDefault();
        props.onClose();
      } else if (e.key === "Tab") {
        const focusables = focusableElements(dialogRef);
        if (focusables.length === 0) return;
        const first = focusables.at(0);
        const last = focusables.at(-1);
        if (!first || !last) return;
        const active = document.activeElement;

        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    globalThis.addEventListener("keydown", handler);

    onCleanup(() => {
      globalThis.removeEventListener("keydown", handler);
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = "";
      // Restaurer le focus si l'élément déclencheur existe encore
      previousFocus?.focus?.();
    });
  });

  return (
    <Show when={props.open}>
      <Portal>
        <div
          class="fixed inset-0 z-50 flex items-end justify-center px-0 py-0 sm:items-center sm:px-4 sm:py-8"
          style={{ background: "var(--bg-overlay)" }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && dismissable()) props.onClose();
          }}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={props.description ? descId : undefined}
            class={`flex max-h-[100dvh] w-full ${props.maxWidth ?? "max-w-2xl"} flex-col rounded-t-2xl border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-[var(--shadow-strong)] motion-safe:animate-[dialogIn_150ms_ease-out] sm:max-h-[90vh] sm:rounded-xl`}
          >
            <header class="flex items-start justify-between gap-4 border-b border-[var(--border-default)] px-4 pt-4 pb-3 sm:px-6 sm:pt-5 sm:pb-4">
              <div class="min-w-0 flex-1">
                <h2
                  id={titleId}
                  class="font-display text-xl font-semibold leading-tight text-[var(--fg-strong)]"
                >
                  {props.title}
                </h2>
                <Show when={props.description}>
                  <p id={descId} class="mt-1 text-sm text-[var(--fg-muted)]">
                    {props.description}
                  </p>
                </Show>
              </div>
              <Show when={dismissable()}>
                <button
                  type="button"
                  onClick={props.onClose}
                  aria-label={t("common.cancel")}
                  class="-mr-2 shrink-0 rounded-md p-1.5 text-[var(--fg-muted)] transition hover:bg-[var(--bg-elevated)] hover:text-[var(--fg-strong)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent-alt)]"
                >
                  <X size={18} stroke-width={1.5} />
                </button>
              </Show>
            </header>

            <div class="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">{props.children}</div>

            <Show
              when={props.footer}
              fallback={
                <footer class="flex justify-end gap-2 border-t border-[var(--border-default)] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-4">
                  <Button variant="secondary" onClick={props.onClose}>
                    {t("common.cancel")}
                  </Button>
                </footer>
              }
            >
              <footer class="flex flex-wrap items-center justify-end gap-2 border-t border-[var(--border-default)] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-4">
                {props.footer}
              </footer>
            </Show>
          </div>
        </div>
      </Portal>
    </Show>
  );
}

/**
 * Sélecteur des éléments focusables internes au container.
 * Ordre DOM préservé pour la navigation Tab naturelle.
 */
function focusableElements(root: HTMLElement | undefined): HTMLElement[] {
  if (!root) return [];
  const selector = [
    "a[href]",
    "button:not([disabled])",
    "textarea:not([disabled])",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
  ].join(",");
  return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
    (el) => !el.hasAttribute("aria-hidden") && el.offsetParent !== null
  );
}
